import * as React from 'react';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import dayjs from 'dayjs';

import { SparkLineChart } from '@mui/x-charts/SparkLineChart';

function getDaysInMonth(month, year) {
  const date = new Date(year, month, 0);
  const monthName = date.toLocaleDateString('en-US', {
    month: 'short',
  });
  const daysInMonth = date.getDate();
  const days = [];
  let i = 1;
  while (days.length < daysInMonth) {
    days.push(`${monthName} ${i}`);
    i += 1;
  }
  return days;
}

function renderSparklineCell(params) {
  const data = getDaysInMonth(10, 2024);
  const { value, colDef } = params;

  if (!value || value.length === 0) {
    return null;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <SparkLineChart
        data={value}
        width={colDef.computedWidth || 100}
        height={32}
        plotType="bar"
        showHighlight
        showTooltip
        colors={['hsl(210, 98%, 42%)']}
        xAxis={{
          scaleType: 'band',
          data,
        }}
      />
    </div>
  );
}

function renderStatus(status) {
  const colors = {
    Online: 'success',
    Offline: 'default',
  };

  return <Chip label={status} color={colors[status]} size="small" />;
}

export function renderAvatar(params) {
  if (params.value == null) {
    return '';
  }

  return (
    <Avatar
      sx={{
        backgroundColor: params.value.color,
        width: '24px',
        height: '24px',
        fontSize: '0.85rem',
      }}
    >
      {params.value.name.toUpperCase().substring(0, 1)}
    </Avatar>
  );
}

export const columns = [
  { field: 'packID', headerName: 'ID', flex: 0.75, minWidth: 150 },
  {
    field: 'status',
    headerName: 'Status',
    flex: 0.5,
    minWidth: 100,
    renderCell: (params) => renderStatus(params.value),
  },
  {
    field: 'imei',
    headerName: 'IMEI',
    headerAlign: 'right',
    align: 'right',
    flex: 0.75,
    minWidth: 150,
  },
  {
    field: 'model_bms',
    headerName: 'BMS',
    headerAlign: 'right',
    align: 'right',
    flex: 0.75,
    minWidth: 150,
  },
  {
    field: 'assignedTo',
    headerName: 'Assigned to',
    headerAlign: 'right',
    align: 'right',
    flex: 0.5,
    minWidth: 100,
  },
  {
    field: 'createdAt',
    headerName: 'Created at',
    headerAlign: 'right',
    align: 'right',
    flex: 1,
    minWidth: 100,
    valueFormatter: (params) => { // Ensure params.value is a valid date
      if (params) {
        return dayjs(params).format('DD-MM-YY hh:mm A');
      } return '';
    }
  },
  {
    field: 'packetHistory',
    headerName: 'Packet history',
    flex: 0.5,
    minWidth: 150,
    renderCell: renderSparklineCell,
  },
];

export const rows = [
  {
    id: 1,
    packID: 'Homepage Overview',
    status: 'Online',
    soc: 8345,
    imei: 212423,
    assignedTo: 18.5,
    createdAt: '2m 15s',
    packetHistory: [
      469172, 488506, 592287, 617401, 640374, 632751, 668638, 807246, 749198, 944863,
      911787, 844815, 992022, 1143838, 1446926, 1267886, 1362511, 1348746, 1560533,
      1670690, 1695142, 1916613, 1823306, 1683646, 2025965, 2529989, 3263473,
      3296541, 3041524, 2599497,
    ],
  },
  {
    id: 2,
    packID: 'Product Details - Gadgets',
    status: 'Online',
    soc: 5653,
    imei: 172240,
    assignedTo: 9.7,
    createdAt: '2m 30s',
    packetHistory: [
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      557488, 1341471, 2044561, 2206438,
    ],
  },
  {
    id: 3,
    packID: 'Checkout Process - Step 1',
    status: 'Offline',
    soc: 3455,
    imei: 58240,
    assignedTo: 15.2,
    createdAt: '2m 10s',
    packetHistory: [
      166896, 190041, 248686, 226746, 261744, 271890, 332176, 381123, 396435, 495620,
      520278, 460839, 704158, 559134, 681089, 712384, 765381, 771374, 851314, 907947,
      903675, 1049642, 1003160, 881573, 1072283, 1139115, 1382701, 1395655, 1355040,
      1381571,
    ],
  },
  {
    id: 4,
    packID: 'User Profile Dashboard',
    status: 'Online',
    soc: 112543,
    imei: 96240,
    assignedTo: 4.5,
    createdAt: '2m 40s',
    packetHistory: [
      264651, 311845, 436558, 439385, 520413, 533380, 562363, 533793, 558029, 791126,
      649082, 566792, 723451, 737827, 890859, 935554, 1044397, 1022973, 1129827,
      1145309, 1195630, 1358925, 1373160, 1172679, 1340106, 1396974, 1623641,
      1687545, 1581634, 1550291,
    ],
  },
  {
    id: 5,
    packID: 'Article Listing - Tech News',
    status: 'Offline',
    soc: 3653,
    imei: 142240,
    assignedTo: 3.1,
    createdAt: '2m 55s',
    packetHistory: [
      251871, 262216, 402383, 396459, 378793, 406720, 447538, 451451, 457111, 589821,
      640744, 504879, 626099, 662007, 754576, 768231, 833019, 851537, 972306,
      1014831, 1027570, 1189068, 1119099, 987244, 1197954, 1310721, 1480816, 1577547,
      1854053, 1791831,
    ],
  },
  {
    id: 6,
    packID: 'FAQs - Customer Support',
    status: 'Online',
    soc: 106543,
    imei: 15240,
    assignedTo: 7.2,
    createdAt: '2m 20s',
    packetHistory: [
      13671, 16918, 27272, 34315, 42212, 56369, 64241, 77857, 70680, 91093, 108306,
      94734, 132289, 133860, 147706, 158504, 192578, 207173, 220052, 233496, 250091,
      285557, 268555, 259482, 274019, 321648, 359801, 399502, 447249, 497403,
    ],
  },
  {
    id: 7,
    packID: 'Product Comparison - Laptops',
    status: 'Offline',
    soc: 7853,
    imei: 32240,
    assignedTo: 6.5,
    createdAt: '2m 50s',
    packetHistory: [
      93682, 107901, 144919, 151769, 170804, 183736, 201752, 219792, 227887, 295382,
      309600, 278050, 331964, 356826, 404896, 428090, 470245, 485582, 539056, 582112,
      594289, 671915, 649510, 574911, 713843, 754965, 853020, 916793, 960158, 984265,
    ],
  },
  {
    id: 8,
    packID: 'Shopping Cart - Electronics',
    status: 'Online',
    soc: 8563,
    imei: 48240,
    assignedTo: 4.3,
    createdAt: '3m 10s',
    packetHistory: [
      52394, 63357, 82800, 105466, 128729, 144472, 172148, 197919, 212302, 278153,
      290499, 249824, 317499, 333024, 388925, 410576, 462099, 488477, 533956, 572307,
      591019, 681506, 653332, 581234, 719038, 783496, 911609, 973328, 1056071,
      1112940,
    ],
  },
  {
    id: 9,
    packID: 'Payment Confirmation - Bank Transfer',
    status: 'Offline',
    soc: 4563,
    imei: 18240,
    assignedTo: 2.7,
    createdAt: '3m 25s',
    packetHistory: [
      15372, 16901, 25489, 30148, 40857, 51136, 64627, 75804, 89633, 100407, 114908,
      129957, 143568, 158509, 174822, 192488, 211512, 234702, 258812, 284328, 310431,
      338186, 366582, 396749, 428788, 462880, 499125, 537723, 578884, 622825,
    ],
  },
  {
    id: 10,
    packID: 'Product Reviews - Smartphones',
    status: 'Online',
    soc: 9863,
    imei: 28240,
    assignedTo: 5.1,
    createdAt: '3m 05s',
    packetHistory: [
      70211, 89234, 115676, 136021, 158744, 174682, 192890, 218073, 240926, 308190,
      317552, 279834, 334072, 354955, 422153, 443911, 501486, 538091, 593724, 642882,
      686539, 788615, 754813, 687955, 883645, 978347, 1142551, 1233074, 1278155,
      1356724,
    ],
  },
  {
    id: 11,
    packID: 'Subscription Management - Services',
    status: 'Offline',
    soc: 6563,
    imei: 24240,
    assignedTo: 4.8,
    createdAt: '3m 15s',
    packetHistory: [
      49662, 58971, 78547, 93486, 108722, 124901, 146422, 167883, 189295, 230090,
      249837, 217828, 266494, 287537, 339586, 363299, 412855, 440900, 490111, 536729,
      580591, 671635, 655812, 576431, 741632, 819296, 971762, 1052605, 1099234,
      1173591,
    ],
  },
  {
    id: 12,
    packID: 'Order Tracking - Shipments',
    status: 'Online',
    soc: 12353,
    imei: 38240,
    assignedTo: 3.5,
    createdAt: '3m 20s',
    packetHistory: [
      29589, 37965, 55800, 64672, 77995, 91126, 108203, 128900, 148232, 177159,
      193489, 164471, 210765, 229977, 273802, 299381, 341092, 371567, 413812, 457693,
      495920, 564785, 541022, 491680, 618096, 704926, 833365, 904313, 974622,
      1036567,
    ],
  },
  {
    id: 13,
    packID: 'Customer Feedback - Surveys',
    status: 'Offline',
    soc: 5863,
    imei: 13240,
    assignedTo: 2.3,
    createdAt: '3m 30s',
    packetHistory: [
      8472, 9637, 14892, 19276, 23489, 28510, 33845, 39602, 45867, 52605, 59189,
      65731, 76021, 85579, 96876, 108515, 119572, 131826, 145328, 160192, 176528,
      196662, 217929, 239731, 262920, 289258, 315691, 342199, 370752, 402319,
    ],
  },
  {
    id: 14,
    packID: 'Account Settings - Preferences',
    status: 'Online',
    soc: 7853,
    imei: 18240,
    assignedTo: 3.2,
    createdAt: '3m 15s',
    packetHistory: [
      15792, 16948, 22728, 25491, 28412, 31268, 34241, 37857, 42068, 46893, 51098,
      55734, 60780, 66421, 72680, 79584, 87233, 95711, 105285, 115814, 127509,
      140260, 154086, 169495, 186445, 205109, 225580, 247983, 272484, 299280,
    ],
  },
  {
    id: 15,
    packID: 'Login Page - Authentication',
    status: 'Offline',
    soc: 9563,
    imei: 24240,
    assignedTo: 2.5,
    createdAt: '3m 35s',
    packetHistory: [
      25638, 28355, 42089, 53021, 66074, 80620, 97989, 118202, 142103, 166890,
      193869, 225467, 264089, 307721, 358059, 417835, 488732, 573924, 674878, 794657,
      938542, 1111291, 1313329, 1543835, 1812156, 2123349, 2484926, 2907023, 3399566,
      3973545,
    ],
  },
  {
    id: 16,
    packID: 'Promotions - Seasonal Sales',
    status: 'Online',
    soc: 13423,
    imei: 54230,
    assignedTo: 7.8,
    createdAt: '2m 45s',
    packetHistory: [
      241732, 256384, 289465, 321423, 345672, 378294, 398472, 420364, 436278, 460192,
      495374, 510283, 532489, 559672, 587312, 610982, 629385, 654732, 678925, 704362,
      725182, 749384, 772361, 798234, 819472, 846291, 872183, 894673, 919283, 945672,
    ],
  },
  {
    id: 17,
    packID: 'Tutorials - How to Guides',
    status: 'Offline',
    soc: 4234,
    imei: 19342,
    assignedTo: 5.2,
    createdAt: '3m 05s',
    packetHistory: [
      12345, 14567, 16789, 18901, 21023, 23145, 25267, 27389, 29501, 31623, 33745,
      35867, 37989, 40101, 42223, 44345, 46467, 48589, 50701, 52823, 54945, 57067,
      59189, 61301, 63423, 65545, 67667, 69789, 71901, 74023,
    ],
  },
  {
    id: 18,
    packID: 'Blog Posts - Tech Insights',
    status: 'Online',
    soc: 8567,
    imei: 34234,
    assignedTo: 6.3,
    createdAt: '2m 50s',
    packetHistory: [
      23456, 25678, 27890, 30102, 32324, 34546, 36768, 38980, 41202, 43424, 45646,
      47868, 50080, 52302, 54524, 56746, 58968, 61180, 63402, 65624, 67846, 70068,
      72290, 74502, 76724, 78946, 81168, 83380, 85602, 87824,
    ],
  },
  {
    id: 19,
    packID: 'Events - Webinars',
    status: 'Offline',
    soc: 3456,
    imei: 19234,
    assignedTo: 4.5,
    createdAt: '3m 20s',
    packetHistory: [
      123456, 145678, 167890, 190012, 212324, 234546, 256768, 278980, 301202, 323424,
      345646, 367868, 390080, 412302, 434524, 456746, 478968, 501180, 523402, 545624,
      567846, 590068, 612290, 634502, 656724, 678946, 701168, 723380, 745602, 767824,
    ],
  },
  {
    id: 20,
    packID: 'Support - Contact Us',
    status: 'Online',
    soc: 6734,
    imei: 27645,
    assignedTo: 3.9,
    createdAt: '2m 55s',
    packetHistory: [
      234567, 256789, 278901, 301023, 323245, 345467, 367689, 389801, 412023, 434245,
      456467, 478689, 500801, 523023, 545245, 567467, 589689, 611801, 634023, 656245,
      678467, 700689, 722801, 745023, 767245, 789467, 811689, 833801, 856023, 878245,
    ],
  },
  {
    id: 21,
    packID: 'Case Studies - Success Stories',
    status: 'Offline',
    soc: 4567,
    imei: 19345,
    assignedTo: 6.1,
    createdAt: '3m 10s',
    packetHistory: [
      34567, 36789, 38901, 41023, 43145, 45267, 47389, 49501, 51623, 53745, 55867,
      57989, 60101, 62223, 64345, 66467, 68589, 70701, 72823, 74945, 77067, 79189,
      81301, 83423, 85545, 87667, 89789, 91901, 94023, 96145,
    ],
  },
  {
    id: 22,
    packID: 'News - Industry Updates',
    status: 'Online',
    soc: 7856,
    imei: 34567,
    assignedTo: 5.7,
    createdAt: '3m 05s',
    packetHistory: [
      45678, 47890, 50102, 52324, 54546, 56768, 58980, 61202, 63424, 65646, 67868,
      70080, 72302, 74524, 76746, 78968, 81180, 83402, 85624, 87846, 90068, 92290,
      94502, 96724, 98946, 101168, 103380, 105602, 107824, 110046,
    ],
  },
  {
    id: 23,
    packID: 'Forum - User Discussions',
    status: 'Offline',
    soc: 5678,
    imei: 23456,
    assignedTo: 4.2,
    createdAt: '2m 40s',
    packetHistory: [
      56789, 58901, 61023, 63145, 65267, 67389, 69501, 71623, 73745, 75867, 77989,
      80101, 82223, 84345, 86467, 88589, 90701, 92823, 94945, 97067, 99189, 101301,
      103423, 105545, 107667, 109789, 111901, 114023, 116145, 118267,
    ],
  },
  {
    id: 24,
    packID: 'Documentation - API Reference',
    status: 'Online',
    soc: 6789,
    imei: 27689,
    assignedTo: 5.0,
    createdAt: '3m 00s',
    packetHistory: [
      67890, 70102, 72324, 74546, 76768, 78980, 81202, 83424, 85646, 87868, 90080,
      92302, 94524, 96746, 98968, 101180, 103402, 105624, 107846, 110068, 112290,
      114502, 116724, 118946, 121168, 123380, 125602, 127824, 130046, 132268,
    ],
  },
  {
    id: 25,
    packID: 'Services - Consulting',
    status: 'Offline',
    soc: 4563,
    imei: 19240,
    assignedTo: 6.4,
    createdAt: '3m 25s',
    packetHistory: [
      345678, 367890, 390012, 412324, 434546, 456768, 478980, 501202, 523424, 545646,
      567868, 590080, 612302, 634524, 656746, 678968, 701180, 723402, 745624, 767846,
      790068, 812290, 834502, 856724, 878946, 901168, 923380, 945602, 967824, 990046,
    ],
  },
  {
    id: 26,
    packID: 'Feedback - User Reviews',
    status: 'Online',
    soc: 8564,
    imei: 34240,
    assignedTo: 6.2,
    createdAt: '3m 15s',
    packetHistory: [
      123478, 145690, 167912, 190134, 212356, 234578, 256790, 279012, 301234, 323456,
      345678, 367890, 390012, 412234, 434456, 456678, 478890, 501012, 523234, 545456,
      567678, 589890, 612012, 634234, 656456, 678678, 700890, 723012, 745234, 767456,
    ],
  },
  {
    id: 27,
    packID: 'Profiles - Team Members',
    status: 'Offline',
    soc: 5634,
    imei: 23423,
    assignedTo: 5.5,
    createdAt: '2m 45s',
    packetHistory: [
      345123, 367345, 389567, 411789, 434012, 456234, 478456, 500678, 522901, 545123,
      567345, 589567, 611789, 634012, 656234, 678456, 700678, 722901, 745123, 767345,
      789567, 811789, 834012, 856234, 878456, 900678, 922901, 945123, 967345, 989567,
    ],
  },
  {
    id: 28,
    packID: 'Notifications - Alerts',
    status: 'Online',
    soc: 6745,
    imei: 27654,
    assignedTo: 4.9,
    createdAt: '3m 10s',
    packetHistory: [
      456123, 478345, 500567, 522789, 545012, 567234, 589456, 611678, 633901, 656123,
      678345, 700567, 722789, 745012, 767234, 789456, 811678, 833901, 856123, 878345,
      900567, 922789, 945012, 967234, 989456, 1011678, 1033901, 1056123, 1078345,
      1100567,
    ],
  },
  {
    id: 29,
    packID: 'Dashboard - Metrics',
    status: 'Offline',
    soc: 5678,
    imei: 23456,
    assignedTo: 6.3,
    createdAt: '2m 50s',
    packetHistory: [
      567890, 590112, 612334, 634556, 656778, 678990, 701212, 723434, 745656, 767878,
      790100, 812322, 834544, 856766, 878988, 901210, 923432, 945654, 967876, 990098,
      1012320, 1034542, 1056764, 1078986, 1101208, 1123430, 1145652, 1167874,
      1190096, 1212318,
    ],
  },
  {
    id: 30,
    packID: 'Reports - Monthly Analysis',
    status: 'Online',
    soc: 7890,
    imei: 34567,
    assignedTo: 5.9,
    createdAt: '3m 20s',
    packetHistory: [
      678901, 701123, 723345, 745567, 767789, 790011, 812233, 834455, 856677, 878899,
      901121, 923343, 945565, 967787, 990009, 1012231, 1034453, 1056675, 1078897,
      1101119, 1123341, 1145563, 1167785, 1190007, 1212229, 1234451, 1256673,
      1278895, 1301117, 1323339,
    ],
  },
  {
    id: 31,
    packID: 'Training - Employee Onboarding',
    status: 'Offline',
    soc: 3456,
    imei: 19234,
    assignedTo: 6.1,
    createdAt: '3m 10s',
    packetHistory: [
      789012, 811234, 833456, 855678, 877890, 900112, 922334, 944556, 966778, 989000,
      1011222, 1033444, 1055666, 1077888, 1100110, 1122332, 1144554, 1166776,
      1188998, 1211220, 1233442, 1255664, 1277886, 1300108, 1322330, 1344552,
      1366774, 1388996, 1411218, 1433440,
    ],
  },
  {
    id: 32,
    packID: 'Resources - Knowledge Base',
    status: 'Online',
    soc: 5678,
    imei: 23456,
    assignedTo: 4.7,
    createdAt: '3m 25s',
    packetHistory: [
      890123, 912345, 934567, 956789, 979012, 1001234, 1023456, 1045678, 1067890,
      1090123, 1112345, 1134567, 1156789, 1179012, 1201234, 1223456, 1245678,
      1267890, 1290123, 1312345, 1334567, 1356789, 1379012, 1401234, 1423456,
      1445678, 1467890, 1490123, 1512345, 1534567,
    ],
  },
  {
    id: 33,
    packID: 'Settings - Privacy Controls',
    status: 'Offline',
    soc: 6789,
    imei: 27689,
    assignedTo: 5.8,
    createdAt: '3m 05s',
    packetHistory: [
      901234, 923456, 945678, 967890, 990112, 1012334, 1034556, 1056778, 1079000,
      1101222, 1123444, 1145666, 1167888, 1190110, 1212332, 1234554, 1256776,
      1278998, 1301220, 1323442, 1345664, 1367886, 1390108, 1412330, 1434552,
      1456774, 1478996, 1501218, 1523440, 1545662,
    ],
  },
  {
    id: 34,
    packID: 'Integrations - Third-Party Services',
    status: 'Online',
    soc: 4567,
    imei: 19345,
    assignedTo: 4.4,
    createdAt: '2m 50s',
    packetHistory: [
      123457, 145679, 167891, 190113, 212335, 234557, 256779, 279001, 301223, 323445,
      345667, 367889, 390011, 412233, 434455, 456677, 478899, 501121, 523343, 545565,
      567787, 590009, 612231, 634453, 656675, 678897, 701119, 723341, 745563, 767785,
    ],
  },
  {
    id: 35,
    packID: 'Account - Billing Information',
    status: 'Offline',
    soc: 7890,
    imei: 34567,
    assignedTo: 5.4,
    createdAt: '3m 00s',
    packetHistory: [
      234568, 256790, 278912, 301134, 323356, 345578, 367790, 390012, 412234, 434456,
      456678, 478890, 501112, 523334, 545556, 567778, 590000, 612222, 634444, 656666,
      678888, 701110, 723332, 745554, 767776, 789998, 812220, 834442, 856664, 878886,
    ],
  },
];
